import Cookies from 'js-cookie';
import { setCookie } from '~/helpers/cookie';
import { NextRouter, useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Store } from 'redux';
import { calculateDays } from '~/helpers/calculateDays';
import clientSide from '~/helpers/clientSide';
import fetch from '~/helpers/fetch';
import { getUtmParamsFromCookie } from '~/helpers/getUtmParamsFromCookie';
import { setNewRelicCurrentRouteName, setNewRelicCustomAttribute, setNewRelicRelease } from '~/helpers/newrelic';
import { trackEvent } from '~/helpers/tracking';
import { setUtmParams, setCallInNumber, setFirebase } from '~/redux/actions/common';
import { resetActiveLegal } from '~/redux/actions/legal';
import { ReduxState } from '~/types/redux';
import { PAGE_ROUTES } from '../../src/constants/pages';
import storeCreator from '../redux/setup/store';
import useCohesion from './useCohesion';
import useFirebase from './useFirebase';
import { getAll } from 'firebase/remote-config';

const production = process.env.environment === 'production';

setNewRelicRelease(process.env.releaseName, process.env.release);

const store = storeCreator(production);

interface UseAppSetup {
  store: Store;
  production: boolean;
  router: NextRouter;
}

const useAppSetup = (): UseAppSetup => {
  const { monarch: monarchState, tracking: trackingState, resume, credit } = store.getState() as ReduxState;
  const { lineItems } = credit?.billingSummary || {};
  const [tracking, setTracking] = useState(trackingState);
  const [monarch, setMonarch] = useState(monarchState);
  const { fetchFireBaseConfig } = useFirebase();

  // Separate effect for tracking updates
  useEffect(() => {
    const unsub = store.subscribe(() => {
      const state = store.getState();
      if (state.tracking) setTracking(state.tracking);
    });

    if (tracking.sessionId) unsub();
    return unsub;
  }, [tracking.sessionId]);

  // Separate effect for monarch/API version updates
  useEffect(() => {
    const unsub = store.subscribe(() => {
      const state = store.getState();
      if (state.monarch?.apiVersion && state.monarch.apiVersion !== monarch?.apiVersion) {
        setNewRelicCustomAttribute([{ name: 'apiVersion', value: state.monarch.apiVersion }]);
        setCookie('apiVersion', state.monarch?.apiVersion, { expires: 30 });
        setMonarch(state.monarch);
      }
    });

    if (monarch?.apiVersion) unsub();
    return unsub;
  }, [monarch?.apiVersion]);

  // Get fuse number from cohesion
  const { fuseNumber } = useCohesion(store.dispatch, tracking);

  // Effect to set call-in number in Redux when fuse number is available
  // Only set if no call-in number is already set (permaleaseNumber takes priority)
  useEffect(() => {
    if (fuseNumber) {
      const currentState = store.getState();
      if (!currentState?.callInNumber) {
        store.dispatch(setCallInNumber(fuseNumber));
      }
    }
  }, [fuseNumber]);

  useEffect(() => {
    if (!clientSide) return;
    // Set UTM params
    const utmParams = getUtmParamsFromCookie();
    if (utmParams) {
      store.dispatch(setUtmParams(utmParams));
      // Set UTM params in NewRelic for tracking
      setNewRelicCustomAttribute([
        { name: 'utm_term', value: utmParams.utm_term || '' },
        { name: 'utm_content', value: utmParams.utm_content || '' },
        { name: 'utm_pp', value: utmParams.utm_pp || '' },
      ]);
    }
  }, []); // Empty dependency array since we only want this to run once on mount

  // NOTE: once resume has been built switch to the resume cookie.
  useEffect(() => {
    if (clientSide && tracking?.sessionId && monarch.apiVersion && !Cookies.get('orderSessionStarted')) {
      setNewRelicCustomAttribute([{ name: 'orderSessionStartedCookieFound', value: 'false' }]);

      fetch({
        endpoint: `${monarch.apiVersion}/order/create`,
        options: {
          method: 'post',
          body: JSON.stringify({
            affiliateId: tracking.affiliateId || '',
            isResume: Boolean(resume?.enabled),
          }),
        },
        tracking,
      });

      trackEvent({ action: 'orderSessionStarted' });
      setCookie('orderSessionStarted', 'true', { expires: 1 / 24 });

      setNewRelicCustomAttribute([{ name: 'orderSessionStartedCookieSet', value: 'true' }]);
    } else {
      setNewRelicCustomAttribute([{ name: 'orderSessionStartedCookieFound', value: 'true' }]);
      setNewRelicCustomAttribute([{ name: 'orderSessionStartedFired', value: 'false' }]);
    }
  }, [tracking.sessionId, monarch.apiVersion]);

  // Event tracking
  const router = useRouter();
  const currentPage = router.route.slice(1) || 'address-check';

  // page tracking.
  useEffect(() => {
    if (clientSide) document.body.dataset.page = router?.pathname?.slice(1) || 'address-check';
  }, [router.pathname]);

  useEffect(() => {
    const state = store.getState();
    const { address, products, tracking, appointment, resume } = state;
    const daysUntilAppointment = calculateDays(appointment?.date);

    store.dispatch(resetActiveLegal());

    setNewRelicCurrentRouteName(currentPage);

    const pageName = resume?.cartPage?.includes('credit')
      ? 'resume_credit'
      : resume?.cartPage?.includes('plan')
      ? 'resume_plan_package'
      : currentPage;

    trackEvent({
      action: 'pageView',
      data: {
        detail: {
          page: currentPage,
          pageName: pageName,
          zip: address?.install?.zip || '',
          products: [products?.selected?.internet?.productId, products?.selected?.voice?.productId].filter((id) => id),
          ropeId: tracking && tracking?.sessionId,
          id: tracking && tracking?.anonymousId,
          daysUntilAppointment,
          serviceability: (() => {
            let serviceability = '';
            if (currentPage !== PAGE_ROUTES.ADDRESS_CHECK) {
              serviceability = products?.activeTab?.copperProduct ? 'SERVICEABLE' : 'SERVICEABLE_FIBER';
            }
            return serviceability;
          })(),
          c2f: currentPage.includes('fiber'),
          resumeEnabled: monarch?.determineResumeQuote || false,
          lineItems: lineItems || {},
          internetId: products?.selected?.internet?.productId,
        },
      },
    });

    if (currentPage === 'address-check' && Cookies?.get('quoteId') && monarch?.determineResumeQuote === false) {
      trackEvent({
        action: 'elementViewed',
        data: {
          elementType: 'PAGE',
          position: pageName,
          text: 'resumeEnabled: false',
        },
      });
    }
  }, [currentPage]);

  useEffect(() => {
    store.dispatch(resetActiveLegal());
    if (currentPage !== 'confirmation') {
      trackEvent({
        action: 'cartEntry',
        data: {
          detail: {
            clientSessionId: tracking.clientSessionId,
            actionName: 'CartEntry',
            orderId: tracking.orderId,
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    fetchFireBaseConfig()
      .then((rc) => {
        const firebaseData = getAll(rc);

        store.dispatch(setFirebase(firebaseData));
      })
      .catch((err) => {
        console.log('firebase error...', err);
      });
  }, []);

  return {
    store,
    production,
    router,
  };
};

export default useAppSetup;
